import { Injectable, computed, signal } from '@angular/core';

import { Address } from './interfaces/address.interface';
import { ConfirmedData } from './interfaces/confirmed-data.interface';
import { Order } from './interfaces/order.interface';
import { Timeslots } from './interfaces/timeslots.interface';
import { UrlParams } from './interfaces/urlparams.interface';

export interface TrackingLocation {
  latitude: string;
  longitude: string;
}

/** Signal-based replacement for the previous NgRx "data" feature store. */
@Injectable({ providedIn: 'root' })
export class OrderStore {
  private readonly orderSignal = signal<Order | null>(null);
  private readonly timeslotsSignal = signal<Timeslots | null>(null);
  private readonly urlParamsSignal = signal<UrlParams | null>(null);
  private readonly confirmedDataSignal = signal<ConfirmedData | null>(null);
  private readonly trackingLocationSignal = signal<TrackingLocation | null>(
    null,
  );

  readonly order = this.orderSignal.asReadonly();
  readonly timeslots = this.timeslotsSignal.asReadonly();
  readonly urlParams = this.urlParamsSignal.asReadonly();
  readonly confirmedData = this.confirmedDataSignal.asReadonly();
  readonly trackingLocation = this.trackingLocationSignal.asReadonly();

  readonly dropOffLocation = computed(
    () => this.orderSignal()?.location ?? null,
  );

  readonly tracking = computed(() => ({
    packageLocation: this.trackingLocationSignal(),
    dropOffLocation: this.orderSignal()?.location ?? null,
  }));

  public setOrder(order: Order): void {
    this.orderSignal.set(order);
  }

  public setTiming(timeslots: Timeslots): void {
    this.timeslotsSignal.set(timeslots);
  }

  public setUrlParams(urlParams: UrlParams): void {
    this.urlParamsSignal.set(urlParams);
  }

  public setConfirmedData(patch: Partial<ConfirmedData>): void {
    this.confirmedDataSignal.update(
      (current) => ({ ...current, ...patch }) as ConfirmedData,
    );
  }

  public setAddressToSave(patch: Partial<Address>): void {
    this.confirmedDataSignal.update((current) => {
      const previous = current?.addressToSave || {};
      return {
        ...current,
        addressToSave: { ...previous, ...patch },
      } as ConfirmedData;
    });
  }

  public removeAddressToSave(): void {
    this.confirmedDataSignal.update(
      (current) => ({ ...current, addressToSave: '' }) as ConfirmedData,
    );
  }

  public setTrackingLocation(location: TrackingLocation): void {
    this.trackingLocationSignal.set(location);
  }
}
